import styled, { useTheme } from 'styled-components'
import { useRef } from 'react'
import { useGetSize } from 'Hooks/useGetSize'
import * as d3 from 'd3'
import { AnimatedNote } from '../AnimatedNote'
import { Note, Label, Tick, ChartTitle } from 'theme/styles'
import { VscArrowSmallDown, VscArrowSmallUp } from 'react-icons/vsc'

const Chart = ({ data, step }: { data: any; step: number }) => {
  //const { breakpoints } = useTheme()
  //const isMobile = useMediaQuery(breakpoints.only('md'))
  const theme = useTheme()
  const isMobile = window.innerWidth < 768
  const containerRef = useRef<HTMLDivElement>(null)
  const svgRef = useRef<SVGSVGElement>(null)
  const { width } = useGetSize(containerRef)
  const height = isMobile ? width * 1.65 : width * 0.75

  const xDomain = [1954, 2024]
  const yDomain = [-15, 25]
  const margin = {
    top: 30,
    right: 10,
    bottom: 30,
    left: isMobile ? 50 : 80,
  }
  const tickXLength = 10

  const xScale = d3
    .scaleLinear()
    .domain(xDomain as any)
    .range([margin.left, width - margin.right])

  const yScale = d3
    .scaleLinear()
    .domain(yDomain)
    .range([height - margin.bottom, margin.top])

  const line = d3
    .line()
    .x((d: any) => xScale(d.year))
    .y((d: any) => yScale(d.mean))
    .curve(d3.curveCatmullRom.alpha(0.2))

  const xTicks = isMobile ? [1960, 1980, 2000, 2020] : [1960, 1980, 2000, 2020]
  const yTicks = [-15, -10, -5, 0, 5, 10, 15, 20]
  const meanLine = line(data.filter((d:any) => d.mean)) || undefined

  return (
    <Container ref={containerRef}>
      <Svg ref={svgRef} width={width} height={height}>
        {/* Y grid */}
        {/* {xTicks.map((value) => (
          <GridLine
            x1={xScale(value)}
            x2={xScale(value)}
            y1={margin.top}
            y2={height - margin.bottom}
          />
        ))} */}

        {/* X ticks */}
        {xTicks.map((value) => (
          <Tick
            x1={xScale(value)}
            x2={xScale(value)}
            y1={height - margin.bottom + tickXLength}
            y2={height - margin.bottom}
          />
        ))}

        {/* X grid */}
        {yTicks.map((value) => {
          return (
            <GridLine
              x1={0}
              x2={width - margin.right}
              y1={yScale(value)}
              y2={yScale(value)}
            />
          )
        })}

        <BabyBoomerArea
          style={{
            opacity: [2, 3].includes(step) ? 0.25 : 0,
          }}
          x={margin.left * 0.5}
          y={yScale(3.5)}
          width={width}
          height={yScale(7) - yScale(13.85)}
        />

        {data.map((value: any) => {
          const dir = value.index > 0 ? 1 : -1
          const height = Math.abs(yScale(0) - yScale(value.index))

          return (
            <rect
              style={{ transition: 'fill 0.75s ease-in-out' }}
              x={xScale(value.year)}
              y={dir === -1 ? yScale(0) : yScale(0) - height}
              width={(width - margin.left - margin.right) / data.length - 1}
              height={height}
              fill={
                [3].includes(step)
                  ? `rgba(${theme.greyLight})`
                  : dir === -1
                  ? // ? 'var(--text-color)'
                    `rgba(${theme.yellowColor}, 0.35)`
                  : `rgba(${theme.yellowColor}, 1)`
              }
            />
          )
        })}

        <mask id='path-mask'>
          <path
            d={meanLine}
            style={{
              opacity: step === 3 ? 1 : 0,
            }}
            fill='none'
            stroke={`rgb(${'255,255,255'}`}
            strokeWidth='4'
            strokeLinecap='round'
          />
        </mask>

        <rect
          x={0}
          y={0}
          width={width}
          height={yScale(0)}
          fill={`rgb(${theme.yellowColor})`}
          mask={`url(#path-mask)`}
        />
        <rect
          x={0}
          y={yScale(0)}
          width={width}
          height={height - yScale(0) - margin.bottom}
          fill={`rgba(${theme.yellowColor}, 0.35)`}
          // fill='var(--text-color)'
          mask={`url(#path-mask)`}
        />

        <MarkerLine
          x1={0}
          x2={width - margin.right}
          y1={yScale(0)}
          y2={yScale(0)}
        />

        <BottomLine
          x1={0}
          x2={width - margin.right}
          y1={height - margin.bottom}
          y2={height - margin.bottom}
        />

        <Marker
          style={{
            opacity: [2, 3].includes(step) ? 1 : 0,
          }}
          x1={xScale(80)}
          x2={xScale(80)}
          y1={margin.top}
          y2={height - margin.bottom}
        />
      </Svg>

      {/* Y labels */}
      {yTicks.map((value) => (
        <Label
          style={{
            top: yScale(value) - 22,
            left: 0,
            transform: 'translateX(0)',
            textAlign: 'right',
            width: 25,
          }}
        >
          {value}
        </Label>
      ))}

      <Label
        style={{
          top: yScale(150000) - 48,
          left: 0,
          transform: 'translateX(0)',
          textAlign: 'left',
          letterSpacing: 0.85,
          width: 150,
        }}
      >
        <strong>Population</strong>
      </Label>

      {/* X labels */}
      {xTicks.map((value) => (
        <Label style={{ left: xScale(value), top: height - 6 }}>{value}</Label>
      ))}

      <AnimatedNote
        chartDimensions={[width, height]}
        show={[2].includes(step)}
        arrowHeadposition={[xScale(1985), yScale(-3)]}
        textPosition={[xScale(1990), yScale(-8)]}
      >
        Déviation normale
      </AnimatedNote>

      <AnimatedNote
        chartDimensions={[width, height]}
        show={[3].includes(step)}
        arrowHeadposition={[xScale(1985), yScale(13) - 5]}
        textPosition={[xScale(1992), yScale(15)]}
      >
        Moyenne pondérée sur 5 ans
      </AnimatedNote>

      <NoteMen
        style={{
          top: yScale(22),
          right: margin.right,
          position: 'absolute',
          fontWeight: 'bold',
        }}
      >
        Plus tardif
        <ArrowIcon>
          <VscArrowSmallUp />
        </ArrowIcon>
      </NoteMen>

      <NoteMen
        style={{
          top: height - margin.top - margin.bottom,
          right: 0,
          position: 'absolute',
          fontWeight: 'bold',
        }}
      >
        Plus précoce
        <ArrowIcon>
          <VscArrowSmallDown />
        </ArrowIcon>
      </NoteMen>

      <ChartTitle
        style={{
          top: 0,
          left: 0,
          transform: 'translateX(0)',
          textAlign: 'left',
          letterSpacing: 0.85,
          width: 300,
        }}
      >
        <strong>Déviation de la moyenne en jours</strong>
        <br />
        Période de référence: 1990 — 2020
      </ChartTitle>

      {/* <MainNote
        style={{
          top: yScale(50000),
          left: xScale(30),
        }}
      >
        Répartition de la population <strong>en {year}</strong>
      </MainNote> */}
    </Container>
  )
}

const Container = styled.div`
  width: 100%;
  position: relative;
`

const GridLine = styled.line`
  // stroke: ${({ theme }) => `rgba(${theme.textColorOnBackground}, 0.1)`};
  stroke: var(--text-color);
  opacity: 0.1;
  stroke-width: 1px;
`

const Marker = styled.line`
  // stroke: ${({ theme }) => `rgb(${theme.textColorOnBackground})`};
  stroke: var(--text-color);
  stroke-width: 2px;
  stroke-dasharray: 2px 5px;
  stroke-linecap: round;
  transition: opacity 0.3s ease;
`

const BabyBoomerArea = styled.rect`
  fill: ${({ theme }) => `rgba(${theme.yellowColor}, 0.65)`};
  opacity: 0.25;
  stroke: none;
  mix-blend-mode: multiply;
  transition: opacity 0.3s ease;
`

const BottomLine = styled.line`
  // stroke: ${({ theme }) => `rgb(${theme.textColorOnBackground})`};
  stroke: var(--text-color);
  stroke-width: 1.25px;
`

const NoteMen = styled(Note)`
  // color: ${({ theme }) => `rgb(${theme.textColorOnBackground})`};
  stroke: var(--text-color);
  font-weight: bold;
  display: flex;
  align-items: center;
  text-align: right;
  justify-content: flex-end;
`

const Svg = styled.svg`
  display: block;
`

const ArrowIcon = styled.div`
  font-size: 1.5em;
`

const MarkerLine = styled.line`
  // stroke: ${({ theme }) => `rgb(${theme.textColorOnBackground})`};
  stroke: var(--text-color);
  stroke-dasharray: 0px 5px;
  stroke-linecap: round;
  stroke-width: 2px;
`

export default Chart
